/**
 * The Erlang/OTP version tree.
 *
 * `create-versions` emits the raw facts: which application versions each
 * Erlang/OTP version contains, when it was tagged, and which CVEs were fixed
 * in which application version. Everything structural is derived here, because
 * a version number on its own determines it:
 *
 *   - which branch a version sits on, and what that branch is based on;
 *   - how two versions are ordered, or that they are not ordered at all;
 *   - and, from that ordering, whether a release still carries an advisory.
 *
 * Those rules are in ./otp-version-scheme.ts, apart from the page so that they
 * can be tested without one.
 */
import { compare, branchOf, baseOf } from "./otp-version-scheme";
/** The VEX justifications that appear in openvex.table, in plain English. */
const JUSTIFICATION = {
    vulnerable_code_not_present: "the vulnerable code is not in the bundled copy",
    vulnerable_code_not_in_execute_path: "the vulnerable code is bundled but never reached",
    component_not_present: "the component is not bundled",
    under_investigation: "still being assessed",
};
const SEVERITY_RANK = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
};
/**
 * Markup that has already been built and must be interpolated verbatim. Held in
 * a wrapper rather than a bare string so that the one thing you cannot do by
 * accident is emit unescaped text.
 */
class Markup {
    value;
    constructor(value) {
        this.value = value;
    }
    toString() {
        return this.value;
    }
}
const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
/** Interpolated values, by type: nested markup verbatim, everything else escaped. */
function interpolate(value) {
    if (value === null || value === undefined || value === false)
        return "";
    if (value instanceof Markup)
        return value.value;
    if (Array.isArray(value))
        return value.map(interpolate).join("");
    return escapeHtml(String(value));
}
/**
 * Builds markup from a template literal, escaping every interpolation unless it
 * is itself markup. Nesting and arrays work as you would expect, so a list of
 * rows is `${rows.map(row)}`.
 */
function html(strings, ...values) {
    let out = strings[0];
    for (let i = 0; i < values.length; i++)
        out += interpolate(values[i]) + strings[i + 1];
    return new Markup(out);
}
/** Escape hatch for markup from elsewhere, such as an inline SVG constant. */
const raw = (value) => new Markup(value);
/**
 * An outlined shield, so the marker reads as "security" rather than as a
 * generic warning. Takes its colour from the surrounding severity class.
 */
const SHIELD = raw('<svg viewBox="0 0 20 22" width="13" height="14" aria-hidden="true" focusable="false">' +
    '<path d="M10 1.2 2.6 4v7.1c0 4.6 3 8.4 7.4 9.7 4.4-1.3 7.4-5.1 7.4-9.7V4L10 1.2z" ' +
    'fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>' +
    '<path d="M10 7.2v4.6" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>' +
    '<circle cx="10" cy="15.4" r="1.15" fill="currentColor"/></svg>');
/** Branches longer than this collapse to their newest release. */
const COLLAPSE_AT = 4;
/**
 * How many major releases receive security updates. Erlang/OTP supports the
 * last three, counting the current one, per the repository security policy:
 * https://github.com/erlang/otp/blob/master/SECURITY.md
 */
const SUPPORTED_MAJORS = 3;
/** Query parameter holding the selected version, so a view can be linked to. */
const VERSION_PARAM = "v";
// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
class VersionTree {
    data;
    tickets;
    root;
    nodes = [];
    byName = new Map();
    position = new Map();
    majors = [];
    branchesByBase = new Map();
    appIndex = new Map();
    advisoriesByCve = new Map();
    bundledByCve = new Map();
    notAffectedByMajor = new Map();
    appKeys = [];
    selected;
    openMajors = new Set();
    openBranches = new Set();
    /** What the tree is currently picking out: an application version, or a CVE. */
    highlight = null;
    compareWith = "";
    /**
     * Advisories the reader has opened. Kept apart from the highlight: hiding the
     * releases an advisory affects should not also fold the advisory away.
     */
    expandedCves = new Set();
    /**
     * Whether the detail sheet is up. Only means anything where the layout has
     * stacked and the panel is a sheet over the foot of the window; in the
     * sidebar the panel is always there. A version is selected from the start, so
     * this begins closed and is raised by the reader choosing one.
     */
    sheetOpen = false;
    /**
     * Whether the reader has picked a version. A version is held from the start
     * so that everything has something to compare against, but until one has been
     * asked for the page shows no relations and no detail: a page that opens with
     * a release already picked out is answering a question nobody put to it.
     */
    chosen = false;
    hits = [];
    /** Index of the result the arrow keys are on, or -1 for none. */
    activeHit = -1;
    /** Where the generated per-release pages live, from the page's Liquid. */
    patchesBase;
    constructor(data, tickets, root) {
        this.data = data;
        this.tickets = tickets;
        this.root = root;
        this.patchesBase = root.dataset.patches ?? "/patches/";
        this.build();
        this.selected = this.nodes[this.nodes.length - 1].vsn;
        this.bind();
        const requested = this.versionFromUrl();
        if (requested) {
            // Arriving on a link: open only the release that version belongs to, and
            // put the legend at the top of the window, so the tree starts just below
            // it rather than the reader having to find a row somewhere down the page.
            this.select(requested, { raise: false });
            this.root.querySelector(".otpv-legend")?.scrollIntoView({ block: "start", behavior: "instant" });
        }
        else {
            // Otherwise the releases that still receive updates are open to begin with.
            this.majors.slice(0, SUPPORTED_MAJORS).forEach((m) => this.openMajors.add(m.n));
            this.render();
        }
    }
    /** The version named by the query string, if it is one we know. */
    versionFromUrl() {
        const raw = new URLSearchParams(window.location.search).get(VERSION_PARAM);
        if (!raw)
            return null;
        const v = raw.trim().replace(/^(erlang\/)?otp[- ]?(?=\d)/i, "");
        return this.byName.has(v) ? v : null;
    }
    // -- derivation ----------------------------------------------------------
    build() {
        const { appVersions, versions, advisories } = this.data;
        const byMajor = new Map();
        const byCve = new Map();
        for (const a of advisories) {
            const forMajor = byMajor.get(a.major);
            if (forMajor)
                forMajor.push(a);
            else
                byMajor.set(a.major, [a]);
            const forCve = byCve.get(a.cve);
            if (forCve)
                forCve.push(a);
            else
                byCve.set(a.cve, [a]);
        }
        this.advisoriesByCve = byCve;
        // Advisories whose CVE record describes releases directly. Every advisory
        // is considered, not only those openvex mentions, and they apply to every
        // release in the tree rather than to one major.
        const byRelease = Object.entries(this.data.cves).filter(([, c]) => c.vulnerable.length);
        this.nodes = versions.map((r) => {
            const apps = new Map();
            for (const i of r.changed.concat(r.same)) {
                const s = appVersions[i];
                const dash = s.lastIndexOf("-");
                apps.set(s.slice(0, dash), s.slice(dash + 1));
            }
            const major = r.vsn.split(".")[0];
            const node = {
                ...r,
                major,
                branch: branchOf(r.vsn),
                apps,
                open: [],
                undetermined: [],
            };
            // Where the CVE record says which releases are affected, it is the better
            // answer: it reaches back to the release that introduced the flaw, while
            // openvex only describes the majors still being updated.
            const decided = new Set();
            for (const [cve, record] of byRelease) {
                decided.add(cve);
                if (record.vulnerable.includes(r.vsn))
                    node.open.push(cve);
            }
            // Otherwise a release still carries an advisory when its version of the
            // affected application is ordered below the version that fixed it. Where
            // the two have no order, nothing can be concluded either way.
            for (const a of byMajor.get(major) ?? []) {
                if (decided.has(a.cve))
                    continue;
                const have = apps.get(a.app);
                if (have === undefined)
                    continue;
                const toFix = compare(have, a.fixed);
                const fromIntro = compare(have, a.introduced);
                if (toFix !== null && toFix >= 0)
                    continue;
                if (toFix !== null && toFix < 0 && fromIntro !== null && fromIntro >= 0) {
                    node.open.push(a.cve);
                    decided.add(a.cve);
                }
                else if (!node.undetermined.includes(a.cve)) {
                    node.undetermined.push(a.cve);
                }
            }
            return node;
        });
        this.nodes.forEach((n, i) => {
            this.byName.set(n.vsn, n);
            this.position.set(n.vsn, i);
            for (const ix of n.changed.concat(n.same)) {
                const key = appVersions[ix];
                const list = this.appIndex.get(key);
                if (list)
                    list.push(n.vsn);
                else
                    this.appIndex.set(key, [n.vsn]);
            }
        });
        this.appKeys = appVersions; // sorted by the source data
        for (const b of this.data.bundledAffected ?? []) {
            const list = this.bundledByCve.get(b.cve);
            if (list)
                list.push(b);
            else
                this.bundledByCve.set(b.cve, [b]);
        }
        // An assessment can be overtaken by events: openvex.table still carries
        // CVE-2025-58050 against the bundled PCRE2 as "under investigation", while
        // the same CVE already has a resolved advisory against erts with a fix
        // version. The resolved advisory wins, or the page would report the same
        // vulnerability as both open and unassessed.
        const resolved = new Set(this.data.advisories.map((a) => `${a.cve}@${a.major}`));
        for (const n of this.data.notAffected) {
            if (resolved.has(`${n.id}@${n.major}`))
                continue;
            const list = this.notAffectedByMajor.get(n.major);
            if (list)
                list.push(n);
            else
                this.notAffectedByMajor.set(n.major, [n]);
        }
        // Group into majors, each with its trunk run and the branches off it.
        const majorMap = new Map();
        const branchMap = new Map();
        for (const n of this.nodes) {
            let m = majorMap.get(n.major);
            if (!m) {
                m = { n: n.major, all: [], trunk: [], branches: [], from: null, to: null };
                majorMap.set(n.major, m);
                this.majors.push(m);
            }
            m.all.push(n);
            if (n.branch === "trunk") {
                m.trunk.push(n);
            }
            else {
                let b = branchMap.get(n.branch);
                if (!b) {
                    b = { id: n.branch, base: baseOf(n.branch), rows: [], oneOff: false };
                    branchMap.set(n.branch, b);
                    m.branches.push(b);
                }
                b.rows.push(n);
            }
        }
        this.majors.sort((a, b) => Number(b.n) - Number(a.n));
        for (const m of this.majors) {
            const dates = m.all.map((n) => n.date).filter((d) => d !== null).sort();
            m.from = dates[0] ?? null;
            m.to = dates[dates.length - 1] ?? null;
            // Newest first, matching how the tree is read.
            m.trunk.reverse();
            m.branches.forEach((b) => b.rows.reverse());
            // A release's maintenance branch is the one cut from its last main-track
            // version, when the next release took over. Anything else is a one-off.
            const lastOnMainTrack = m.trunk[0]?.vsn;
            for (const b of m.branches) {
                const base = this.byName.get(b.base);
                b.oneOff = !base || base.branch !== "trunk" || b.base !== lastOnMainTrack;
            }
        }
        for (const b of branchMap.values()) {
            const list = this.branchesByBase.get(b.base);
            if (list)
                list.push(b);
            else
                this.branchesByBase.set(b.base, [b]);
        }
    }
    /** The version immediately before this one on its own line. */
    predecessor(n) {
        const major = this.majors.find((m) => m.n === n.major);
        const line = n.branch === "trunk"
            ? major.trunk
            : major.branches.find((b) => b.id === n.branch).rows;
        const i = line.indexOf(n);
        if (i < line.length - 1)
            return line[i + 1];
        if (n.branch !== "trunk")
            return this.byName.get(baseOf(n.branch)) ?? null;
        const older = this.majors[this.majors.indexOf(major) + 1];
        return older ? older.trunk[0] : null;
    }
    relation(v) {
        if (v === this.selected)
            return "sel";
        const c = compare(v, this.selected);
        if (c === null)
            return "un";
        return c < 0 ? "less" : "gt";
    }
    worstSeverity(cves) {
        let worst = null;
        for (const cve of cves) {
            const severity = this.data.cves[cve]?.severity;
            if (severity && (!worst || SEVERITY_RANK[severity] > SEVERITY_RANK[worst])) {
                worst = severity;
            }
        }
        return worst;
    }
    matchesHighlight(n) {
        const h = this.highlight;
        if (!h)
            return false;
        if ("app" in h)
            return n.changed.includes(h.app) || n.same.includes(h.app);
        if ("cve" in h)
            return n.open.includes(h.cve);
        return (this.tickets[h.ticket] ?? []).includes(n.vsn);
    }
    /**
     * What the highlight is picking out, in words, since the tree alone does not
     * say why those rows and not others.
     */
    highlightSummary() {
        const h = this.highlight;
        if (!h)
            return null;
        const matched = this.nodes.filter((n) => this.matchesHighlight(n)).length;
        if ("app" in h)
            return html `<b>${this.data.appVersions[h.app]}</b> is in ${matched} releases`;
        if ("cve" in h)
            return html `<b>${h.cve}</b> is still open in ${matched} releases`;
        const releases = this.tickets[h.ticket] ?? [];
        const listed = releases.slice(0, 4).join(", ");
        const rest = releases.length > 4 ? ` and ${releases.length - 4} more` : "";
        return html `<b>${h.ticket}</b> was fixed in ${listed}${rest}`;
    }
    /** Names the release to move to, which is the actionable half of an advisory. */
    fixedInHtml(cve, from) {
        const fix = this.fixedIn(cve, from);
        if (!fix)
            return "";
        return fix.onThisLine
            ? html `<span class="otpv-fix">first fixed in <b>Erlang/OTP ${fix.version}</b>, which is a
          descendant of ${from.vsn}</span>`
            : html `<span class="otpv-fix">fixed in <b>Erlang/OTP ${fix.version}</b>, which has no order against
          ${from.vsn} &mdash; moving there is not guaranteed to keep what you have</span>`;
    }
    /**
     * The applications an advisory concerns. Openvex files it per major and per
     * application, so a release outside those majors falls back to whatever the
     * advisory names anywhere.
     */
    appsFor(cve, major) {
        const all = this.advisoriesByCve.get(cve) ?? [];
        const here = all.filter((a) => a.major === major);
        const named = [...new Set((here.length ? here : all).map((a) => a.app))];
        // openvex only covers the majors still updated, so an advisory it has not
        // caught up with falls back to what the CVE record names.
        if (named.length)
            return named;
        const fromRecord = Object.keys(this.data.cves[cve]?.applications ?? {});
        if (fromRecord.length)
            return fromRecord;
        // Reached through a bundled component: name the application that ships it.
        return [...new Set((this.bundledByCve.get(cve) ?? []).flatMap((b) => b.apps))];
    }
    /**
     * The version of an application that fixes an advisory, for a release
     * carrying `have`. Taken from the CVE record where it says so, since openvex
     * only describes the majors it covers; null when this version is not affected.
     */
    applicationFix(cve, app, have) {
        for (const range of this.data.cves[cve]?.applications?.[app] ?? []) {
            const since = compare(have, range.from);
            if (since === null || since < 0)
                continue;
            if (range.until) {
                const c = compare(have, range.until);
                if (c !== null && c < 0)
                    return range.until;
                continue;
            }
            // An open range is fixed at one point per maintenance line. If none of
            // them is at or below what is installed, the relevant fix is the earliest
            // ordered above it.
            const fixes = range.fixedAt ?? [];
            if (fixes.some((f) => { const c = compare(have, f); return c !== null && c >= 0; }))
                continue;
            const ahead = fixes
                .filter((f) => { const c = compare(have, f); return c !== null && c < 0; })
                .sort((a, b) => compare(a, b) ?? 0);
            if (ahead.length)
                return ahead[0];
        }
        return null;
    }
    /** Every release still carrying this advisory. */
    affectedBy(cve) {
        return this.nodes.filter((n) => n.open.includes(cve));
    }
    /**
     * The first release that carries the fix and is a descendant of `from`, so
     * upgrading to it is guaranteed to include everything already in hand. Falls
     * back to the earliest release carrying the fix at all, flagged as being off
     * this line, when no descendant has it.
     */
    fixedIn(cve, from) {
        const record = this.data.cves[cve];
        // Where the CVE record names the releases that carry the fix, use those:
        // they are stated rather than inferred, and they cover the lines openvex
        // says nothing about.
        const stated = (record?.fixedAt ?? []);
        const known = stated.filter((v) => this.byName.has(v));
        const carriesFix = known.length
            ? known.map((v) => this.byName.get(v)).sort((x, y) => this.position.get(x.vsn) - this.position.get(y.vsn))
            : this.nodes.filter((n) => (this.advisoriesByCve.get(cve) ?? []).some((a) => {
                const have = n.apps.get(a.app);
                if (have === undefined)
                    return false;
                const c = compare(have, a.fixed);
                return c !== null && c >= 0;
            }));
        const descendant = carriesFix.find((n) => {
            const c = compare(from.vsn, n.vsn);
            return c !== null && c < 0;
        });
        if (descendant)
            return { version: descendant.vsn, onThisLine: true };
        return carriesFix.length ? { version: carriesFix[0].vsn, onThisLine: false } : null;
    }
    /**
     * Picks something out across the tree, opening whatever is needed to make the
     * matches visible.
     */
    setHighlight(h) {
        this.highlight = h;
        if (h) {
            for (const n of this.nodes) {
                if (!this.matchesHighlight(n))
                    continue;
                this.openMajors.add(n.major);
                let branch = n.branch;
                while (branch !== "trunk") {
                    this.openBranches.add(branch);
                    const base = this.byName.get(baseOf(branch));
                    if (!base)
                        break;
                    branch = base.branch;
                }
            }
        }
    }
    /** The branch a version sits on, or undefined for the main track. */
    branchOf(n) {
        return this.majors.find((m) => m.n === n.major)?.branches.find((b) => b.id === n.branch);
    }
    /** Whether a major release still receives security updates. */
    isSupported(major) {
        return Number(major) > Number(this.majors[0].n) - SUPPORTED_MAJORS;
    }
    el(id) {
        return this.root.querySelector("#" + id);
    }
    // -- rendering -----------------------------------------------------------
    render() {
        this.renderTree();
        this.renderDetail();
        // Revealed once there is something to show, so the shell is not left
        // half-rendered while the data loads or if it never arrives.
        this.root.querySelectorAll(".otpv-cols").forEach((e) => (e.hidden = false));
        this.root.querySelectorAll(".otpv-legend").forEach((e) => (e.hidden = !this.chosen));
        // The detail panel runs to a few thousand characters; announcing all of it
        // on every selection would be unusable, so only the change is announced.
        const announce = this.root.querySelector("#otpv-announce");
        if (announce && this.chosen)
            announce.textContent = `Selected Erlang/OTP ${this.selected}`;
    }
    rowHtml(n, isHead = false) {
        const highlighted = this.matchesHighlight(n);
        const changed = n.changed.map((i) => this.data.appVersions[i]);
        const shown = changed.slice(0, 4).join("  ") + (changed.length > 4 ? "  +" + (changed.length - 4) : "");
        const severity = this.worstSeverity(n.open);
        const classes = [
            this.chosen ? this.relation(n.vsn) : "",
            highlighted ? "match" : "",
            isHead ? "head" : "",
            // 17.0 is the root of the whole tree, so the main track stops there.
            n === this.nodes[0] ? "root" : "",
        ].filter(Boolean);
        const openCount = `${n.open.length} open advisor${n.open.length === 1 ? "y" : "ies"}`;
        const warning = severity
            ? html ` title="${openCount}, worst severity ${severity}"`
            : "";
        return html `
      <div class="otpv-row ${classes.join(" ")}" data-v="${n.vsn}">
        <span class="otpv-spine"><span class="otpv-dot"></span></span>
        <button class="otpv-row-btn" type="button">
          <span class="otpv-v">${n.vsn}</span>
          <span class="otpv-warn ${severity ?? ""}"${warning}>${severity ? SHIELD : ""}</span>
          <span class="otpv-apps">${changed.length ? shown : raw("&mdash;")}</span>
          <span class="otpv-date">${n.date ?? ""}</span>
        </button>
      </div>`;
    }
    /**
     * A branch is drawn above the version it grew out of, since every release on
     * it is newer than that version. Branches off branches nest the same way.
     */
    branchHtml(b) {
        const holdsSelection = this.chosen && b.rows.some((r) => r.vsn === this.selected);
        const collapsible = b.rows.length > COLLAPSE_AT;
        const collapsed = collapsible && !this.openBranches.has(b.id) && !holdsSelection;
        const shown = collapsed ? b.rows.slice(0, 1) : b.rows;
        const hidden = b.rows.length - shown.length;
        // Rows run newest first, so the branch sits above the version it came from.
        const rows = shown.map((r, i) => this.rowsWithBranches(r, i === 0));
        // While releases are hidden they get a node of their own on the line, so the
        // branch reads as continuing past what is shown. Once expanded there is
        // nothing to stand in for, so the control moves onto the branch label.
        const standIn = collapsed
            ? html `
        <div class="otpv-more">
          <span class="otpv-spine"><span class="otpv-dot"></span></span>
          <button class="otpv-more-btn" type="button" data-branch="${b.id}">
            show ${hidden} older release${hidden === 1 ? "" : "s"}
          </button>
        </div>`
            : "";
        const toggle = collapsible && !collapsed
            ? html ` <button class="otpv-more-btn" type="button" data-branch="${b.id}">show fewer</button>`
            : "";
        const whyOneOff = "Cut from a version other than the last on the main track, to resolve a " +
            "particular issue rather than to maintain the release.";
        const kind = b.oneOff
            ? html `<span class="otpv-oneoff" title="${whyOneOff}">one-off</span>`
            : "";
        return html `
      <div class="otpv-branch${b.oneOff ? " one-off" : ""}">
        <div class="otpv-rows">
          ${rows}${standIn}
        </div>
        <div class="otpv-branch-head">
          <span class="otpv-spine"><span class="otpv-elbow"></span></span>
          <span class="otpv-branch-label">
            branch <b>${b.id}</b> &mdash;
            ${b.rows.length} release${b.rows.length === 1 ? "" : "s"} off <b>${b.base}</b>
            ${kind}${toggle}
          </span>
        </div>
      </div>`;
    }
    rowsWithBranches(n, isHead = false) {
        const spurs = (this.branchesByBase.get(n.vsn) ?? []).map((b) => this.branchHtml(b));
        return html `${spurs}${this.rowHtml(n, isHead)}`;
    }
    renderTree() {
        this.root
            .querySelectorAll(".otpv-relv")
            .forEach((e) => (e.textContent = this.selected));
        const v = "Erlang/OTP " + this.selected;
        this.el("otpv-tip-self").textContent =
            `${v} — the version every other version on this page is compared against. ` +
                "Select any version in the tree to compare against that one instead.";
        this.el("otpv-tip-less").textContent =
            `The versions ${v} is built on. Everything released in them is also in it, ` +
                "so moving up from one of them loses nothing.";
        this.el("otpv-tip-gt").textContent =
            `The versions built on ${v}. Each one contains everything in it, so moving ` +
                "to one of them is a strict step forward.";
        this.el("otpv-tip-un").textContent =
            `Neither these nor ${v} is an ancestor of the other, so nothing follows about ` +
                "what either contains — a fix present in one may simply not exist in the other.";
        const first = this.nodes[0].date;
        const last = this.nodes[this.nodes.length - 1].date;
        const span = first && last ? ` &middot; ${first.slice(0, 4)}&ndash;${last.slice(0, 4)}` : "";
        const branches = [...this.branchesByBase.values()].flat().length;
        this.el("otpv-count").innerHTML = String(this.highlightSummary() ??
            html `${this.nodes.length} releases &middot; ${this.majors.length} major versions &middot;
          ${branches} branches${raw(span)}`);
        this.el("otpv-tree").innerHTML = String(html `${this.majors.map((m, i) => {
            const open = this.openMajors.has(m.n);
            const seg = { sel: 0, less: 0, gt: 0, un: 0 };
            m.all.forEach((n) => seg[this.relation(n.vsn)]++);
            const bar = !this.chosen
                ? []
                : ["sel", "gt", "less", "un"]
                    .filter((k) => seg[k])
                    .map((k) => {
                    const width = Math.max(3, Math.round((seg[k] / m.all.length) * 96));
                    return html `<i class="otpv-seg-${k}" style="width:${width}px"></i>`;
                });
            const from = m.from?.slice(0, 4);
            const to = m.to?.slice(0, 4);
            const years = from && to ? ` \u00b7 ${from}${to === from ? "" : "\u2013" + to}` : "";
            const branches = m.branches.length
                ? ` \u00b7 ${m.branches.length} branch${m.branches.length > 1 ? "es" : ""}`
                : "";
            const head = html `
        <button class="otpv-major-btn" type="button" data-major="${m.n}"
                aria-expanded="${open ? "true" : "false"}">
          <span class="otpv-spine${i === this.majors.length - 1 ? " last" : ""}">
            <span class="otpv-node"></span>
          </span>
          <span class="otpv-major-name">
            Erlang/OTP ${m.n}
            <small>${m.all.length} release${m.all.length === 1 ? "" : "s"}${years}${branches} \u00b7 <a href="https://github.com/erlang/otp/tree/maint-${m.n}">maint-${m.n}</a></small>
            ${this.isSupported(m.n) ? "" : html `<span class="otpv-chip eol">no longer supported</span>`}
          </span>
          <span class="otpv-relbar">
            ${bar}<span class="otpv-caret">${open ? raw("&minus;") : "+"}</span>
          </span>
        </button>`;
            const body = open
                ? html `
        <div class="otpv-rows">
          ${m.trunk.map((n) => this.rowsWithBranches(n))}
        </div>`
                : "";
            return html `
      <div class="otpv-major${open ? " open" : ""}${seg.sel ? " on" : ""}">${head}${body}
      </div>`;
        })}`);
    }
    renderDetail() {
        if (!this.chosen) {
            this.el("otpv-detail").innerHTML = String(html `
        <div class="card-header">
          <h5 class="otpv-selected">Pick a version</h5>
          <p class="text-muted mb-0">Choose a release in the tree to see what it contains, which
            versions are guaranteed to contain it, and which advisories it still carries.</p>
        </div>`);
            this.el("otpv-detail").classList.remove("otpv-open");
            return;
        }
        const n = this.byName.get(this.selected);
        const previous = this.predecessor(n);
        const changed = n.changed.map((i) => this.data.appVersions[i]).sort();
        const unchanged = n.same.map((i) => this.data.appVersions[i]).sort();
        const tag = "OTP-" + n.vsn;
        const severity = this.worstSeverity(n.open);
        const newest = this.nodes[this.nodes.length - 1];
        const appRow = (s, dim) => {
            const dash = s.lastIndexOf("-");
            const name = s.slice(0, dash);
            const version = s.slice(dash + 1);
            const before = previous?.apps.get(name);
            const was = !dim && before && before !== version ? html `<s>${before}</s> ` : "";
            return html `<tr>
        <td>${name}</td>
        <td class="otpv-av">${was}${dim ? version : html `<b>${version}</b>`}</td>
      </tr>`;
        };
        const branch = this.branchOf(n);
        const onBranch = n.branch === "trunk"
            ? html `On the <b>main track</b>, where every release was the newest Erlang/OTP at the time.`
            : branch?.oneOff
                ? html `On branch <b>${n.branch}</b>, based on <b>${baseOf(n.branch)}</b> &mdash; a one-off branch,
              cut to resolve a particular issue rather than to maintain the release.`
                : html `On branch <b>${n.branch}</b>, based on <b>${baseOf(n.branch)}</b> &mdash; maintenance
              released after Erlang/OTP ${Number(n.major) + 1} had taken over the main track.`;
        this.el("otpv-detail").innerHTML = String(html `
      <div class="card-header">
        <button class="otpv-sheet-close" type="button" aria-label="Close">&times;</button>
        <h5 class="otpv-selected">Erlang/OTP ${n.vsn}</h5>
        <div class="otpv-meta">
          <span class="otpv-chip plain">${n.date ? "tagged " + n.date : "date unrecorded"}</span>
          <span class="otpv-chip plain">
            ${n.changed.length} of ${n.changed.length + n.same.length} applications changed
          </span>
          ${severity
            ? html `<span class="otpv-chip with-icon sev-${severity}"
                >${SHIELD}${n.open.length} open advisor${n.open.length === 1 ? "y" : "ies"}</span
              >`
            : ""}
          ${this.isSupported(n.major) ? "" : html `<span class="otpv-chip eol">no longer supported</span>`}
        </div>
        <p class="text-muted mb-0">${onBranch}</p>
      </div>

      <div class="otpv-sect">
        <h5 class="border-bottom">Security</h5>
        ${this.securityHtml(n)}
      </div>

      <div class="otpv-sect">
        <h5 class="border-bottom">Is it in there?</h5>
        <input id="otpv-cmp" class="form-control" type="search" spellcheck="false" autocomplete="off"
          value="${this.compareWith}" placeholder="Compare with e.g. ${newest.vsn}">
        ${this.verdictHtml()}
      </div>

      <div class="otpv-sect">
        <h5 class="border-bottom">Upgrading</h5>
        <p>${this.upgradeHtml(n)}</p>
      </div>

      <div class="otpv-sect">
        <h5 class="border-bottom">Applications changed here &mdash; ${changed.length}</h5>
        ${changed.length
            ? html `<table class="table table-sm otpv-apps-list">${changed.map((a) => appRow(a, false))}</table>`
            : html `<p>No application changed in this version.</p>`}
      </div>

      <div class="otpv-sect">
        <details>
          <summary>Unchanged applications &mdash; ${unchanged.length}</summary>
          <table class="table table-sm otpv-apps-list muted">${unchanged.map((a) => appRow(a, true))}</table>
        </details>
      </div>

      <div class="otpv-sect">
        <h5 class="border-bottom">Release</h5>
        <div class="otpv-links">
          <a class="btn btn-sm btn-outline-primary" href="${this.patchesBase + tag}">
            Release notes &amp; downloads
          </a>
          <a class="btn btn-sm btn-outline-secondary"
            href="https://github.com/erlang/otp/releases/tag/${tag}">Git tag</a>
        </div>
        <p class="text-muted mt-3 mb-0">The release page lists the source archive, documentation and Windows
          installers for this version.</p>
      </div>
    `);
        // The card is rebuilt from scratch on every selection, so the sheet's state
        // has to be re-applied rather than left on the element, the same way the
        // opened advisories are.
        this.el("otpv-detail").classList.toggle("otpv-open", this.sheetOpen);
        const input = this.el("otpv-cmp");
        input.addEventListener("input", () => {
            this.compareWith = input.value;
            const caret = input.selectionStart;
            this.renderDetail();
            const next = this.el("otpv-cmp");
            next.focus();
            next.setSelectionRange(caret, caret);
        });
    }
    securityHtml(n) {
        if (!Object.keys(this.data.cves).length) {
            return html `<p class="text-muted">Advisory data is unavailable.</p>`;
        }
        const supported = this.majors
            .slice(0, SUPPORTED_MAJORS)
            .map((m) => m.n)
            .reverse()
            .join(", ");
        const unsupported = this.isSupported(n.major)
            ? ""
            : html `<div class="otpv-eol-note">
          <b>Erlang/OTP ${n.major} no longer receives security updates.</b>
          <p>Erlang/OTP supports the last ${SUPPORTED_MAJORS} releases, currently ${supported}. Newly
            discovered vulnerabilities will not be fixed here.</p>
        </div>`;
        const showing = this.highlight && "cve" in this.highlight ? this.highlight.cve : null;
        const advisories = n.open.length
            ? [...n.open]
                .sort((a, b) => (SEVERITY_RANK[this.data.cves[b]?.severity] ?? 0) -
                (SEVERITY_RANK[this.data.cves[a]?.severity] ?? 0))
                .map((cve) => {
                const record = this.data.cves[cve];
                const applications = this.appsFor(cve, n.major);
                // What this release carries, against the version that fixes it. The
                // CVE record answers for any release; openvex only for the majors
                // it covers, and only once its bot has caught up.
                const versions = applications
                    .map((app) => {
                    const have = n.apps.get(app);
                    if (have === undefined)
                        return null;
                    const openvex = (this.advisoriesByCve.get(cve) ?? []).find((e) => e.app === app && e.major === n.major);
                    const fixed = this.applicationFix(cve, app, have) ?? openvex?.fixed;
                    return fixed ? { app, have, fixed } : null;
                })
                    .filter((v) => v !== null);
                return html `
              <details class="otpv-cve"${this.expandedCves.has(cve) ? raw(" open") : ""}>
                <summary class="otpv-cve-top">
                  <span class="otpv-chip sev-${record?.severity}">${record?.severity}${record?.cvss ? ` ${record.cvss.score}` : ""}</span>
                  ${applications.length ? html `<span class="otpv-cve-app">${applications.join(", ")}</span>` : ""}
                  <span class="otpv-cve-title">${record?.summary ?? cve}</span>
                </summary>
                <span class="otpv-fix"
                  >${cve}${record?.cwe ? ` \u00b7 ${record.cwe.id} ${record.cwe.description}` : ""}</span
                >
                ${versions.map((e) => html `<span class="otpv-fix">${e.app} ${e.have} &lt; <b>${e.fixed}</b> &mdash; not fixed here</span>`)}
                ${(this.bundledByCve.get(cve) ?? []).map((b) => html `<span class="otpv-fix">through bundled ${b.component}, fixed in
                    <b>${b.fixed}</b> of that component</span>`)}
                ${record?.cvss?.vector ? html `<span class="otpv-fix">${record.cvss.vector}</span>` : ""}
                ${this.fixedInHtml(cve, n)}
                ${record?.workaround
                    ? html `
                    <details class="otpv-workaround">
                      <summary>Workaround</summary>
                      <p>${record.workaround}</p>
                    </details>`
                    : ""}
                <div class="otpv-links">
                  <button class="btn btn-sm btn-outline-secondary" type="button" data-cve="${cve}"
                          aria-pressed="${showing === cve ? "true" : "false"}"
                  >${showing === cve ? "Hide affected versions" : "Show affected versions"}</button>
                  ${record?.url
                    ? html `<a class="btn btn-sm btn-outline-primary" href="${record.url}">${record.ghsa ?? "Advisory"}</a>`
                    : ""}
                </div>
              </details>`;
            })
            : html `
        <div class="otpv-safe">
          <b>No open advisories</b>
          <p>No advisory names this release, and none of the application versions it carries is below the
            version that fixed one.</p>
        </div>`;
        // Neither source places these: the CVE record does not say which releases
        // are affected and openvex does not list them at all. Kept apart from the
        // advisories above, because not knowing is not the same as being affected.
        const unplaced = Object.entries(this.data.cves).filter(([cve, record]) => !record.vulnerable.length && !this.advisoriesByCve.has(cve));
        const unplacedHtml = unplaced.length
            ? html `
        <details class="otpv-bundled">
          <summary>Advisories that could not be placed &mdash; ${unplaced.length}</summary>
          <p class="text-muted">Neither the CVE record nor <code>openvex.table</code> says which releases
            these affect, so whether this one is among them is unknown.</p>
          <ul class="otpv-bundled-list">
            ${unplaced.map(([cve, record]) => html `<li>
                <span class="otpv-bundled-id">${cve}</span>
                <span class="otpv-chip plain">${record.severity ?? "unrated"}</span>
                <span class="otpv-bundled-why">${record.summary ?? ""}</span>
              </li>`)}
          </ul>
        </details>`
            : "";
        const undetermined = n.undetermined.length
            ? html `<p class="text-muted">${n.undetermined.length} advisor${n.undetermined.length === 1
                ? "y has"
                : "ies have"} no defined order against this version, so whether it is affected cannot be
          determined.</p>`
            : "";
        return html `${unsupported}${advisories}${undetermined}${unplacedHtml}${this.bundledHtml(n.major)}`;
    }
    /**
     * Vulnerabilities reported against a bundled component. Every one of these is
     * an assessment of the major release rather than of a single version, so it
     * is kept apart from the advisories above and labelled as such.
     */
    bundledHtml(major) {
        const list = this.notAffectedByMajor.get(major);
        if (!list || list.length === 0)
            return "";
        const rows = [...list]
            .sort((a, b) => a.component.localeCompare(b.component) || a.id.localeCompare(b.id))
            .map((a) => {
            const investigating = a.justification === "under_investigation";
            return html `<li>
          <span class="otpv-bundled-id">${a.id}</span>
          <span class="otpv-chip plain">${investigating ? "under investigation" : "not affected"}</span>
          <span class="otpv-bundled-why"
            >${a.component}${a.apps.length ? ` bundled by ${a.apps.join(", ")}` : ""} &mdash;
            ${JUSTIFICATION[a.justification] ?? a.justification}</span
          >
        </li>`;
        });
        return html `<details class="otpv-bundled">
      <summary>Bundled components &mdash; ${list.length}</summary>
      <p class="text-muted">Reported against a third-party component that Erlang/OTP ships, and assessed for
        Erlang/OTP ${major} as a whole rather than for a single version.</p>
      <ul class="otpv-bundled-list">${rows}</ul>
    </details>`;
    }
    verdictHtml() {
        const other = this.compareWith.trim().replace(/^(erlang\/)?otp[- ]?(?=\d)/i, "");
        if (!other)
            return "";
        if (!/^\d+(\.\d+)*$/.test(other)) {
            return html `<div class="otpv-verdict">
        <b>${other}</b>
        <p>Not a version number. Try something like <code>27.3.4.6</code>.</p>
      </div>`;
        }
        const known = this.byName.has(other)
            ? ""
            : html ` <span class="otpv-chip plain">never released</span>`;
        const c = compare(this.selected, other);
        if (c === 0) {
            return html `<div class="otpv-verdict"><b>${this.selected} = ${other}</b><p>The same version.</p></div>`;
        }
        if (c === null) {
            return html `<div class="otpv-verdict un">
        <b>${this.selected} ? ${other}</b>${known}
        <p>No order. Neither is an ancestor of the other, so nothing follows about what either one contains
          &mdash; a fix present in one may simply not exist in the other.</p>
      </div>`;
        }
        if (c < 0) {
            return html `<div class="otpv-verdict gt">
        <b>${this.selected} &lt; ${other}</b>${known}
        <p><b>${other}</b> is a descendant. Everything in ${this.selected} is also in it, so it is a safe move
          from here.</p>
      </div>`;
        }
        return html `<div class="otpv-verdict less">
      <b>${this.selected} &gt; ${other}</b>${known}
      <p><b>${other}</b> is an ancestor. You already have everything it contains.</p>
    </div>`;
    }
    upgradeHtml(n) {
        const major = this.majors.find((m) => m.n === n.major);
        const line = n.branch === "trunk" ? major.trunk : major.branches.find((b) => b.id === n.branch).rows;
        const head = line[0];
        const newest = this.nodes[this.nodes.length - 1];
        if (head.vsn !== n.vsn) {
            return html `<b>${head.vsn}</b> is the newest version on this line and is a descendant of ${n.vsn}
        &mdash; the safe upgrade without leaving the branch.`;
        }
        if (n.vsn !== newest.vsn) {
            return html `This is the newest version on its line. Moving to <b>${newest.vsn}</b> crosses to the main
        track, where the order against ${n.vsn} is
        <b>${compare(n.vsn, newest.vsn) === null ? "undefined" : "defined"}</b>.`;
        }
        return html `The newest Erlang/OTP version. Everything else in the tree is an ancestor of it or has no
      order against it.`;
    }
    // -- interaction ---------------------------------------------------------
    /** Whether the panel is a sheet and up, rather than the sidebar or hidden. */
    isSheet() {
        const style = getComputedStyle(this.el("otpv-detail"));
        return style.position === "fixed" && style.display !== "none";
    }
    closeSheet() {
        this.sheetOpen = false;
        this.el("otpv-detail").classList.remove("otpv-open");
    }
    /**
     * The foot of what the reader can actually see: the top of the sheet where
     * there is one, the foot of the window otherwise.
     */
    visibleBottom() {
        const sheet = this.el("otpv-detail");
        return this.isSheet() ? sheet.getBoundingClientRect().top : window.innerHeight;
    }
    /** Puts the selected row in the middle of the visible area. */
    scrollToSelected(behavior) {
        const row = this.selectedRow();
        if (!row)
            return;
        const box = row.getBoundingClientRect();
        window.scrollBy({ top: box.top + box.height / 2 - this.visibleBottom() / 2, behavior });
    }
    /**
     * A row tapped near the foot of the window is behind the sheet that tapping
     * it has just raised. Moves it out from under, and only that far: a tap
     * should not otherwise shift what the reader is looking at.
     */
    keepSelectedClearOfSheet() {
        if (!this.isSheet())
            return;
        const row = this.selectedRow();
        if (!row)
            return;
        const overlap = row.getBoundingClientRect().bottom - this.visibleBottom();
        if (overlap > 0)
            window.scrollBy({ top: overlap + 12, behavior: "smooth" });
    }
    selectedRow() {
        return this.root.querySelector(`.otpv-row[data-v="${CSS.escape(this.selected)}"]`);
    }
    /**
     * @param opts.raise whether to bring the sheet up on a phone. Default for a
     * selection the reader made by hand; a link or a step through history puts
     * them on the version without laying a panel over the tree they arrived at.
     */
    select(v, opts = {}) {
        const n = this.byName.get(v);
        if (!n)
            return;
        this.selected = v;
        this.chosen = true;
        if (opts.raise !== false)
            this.sheetOpen = true;
        this.openMajors.add(n.major);
        // A version on a branch off a branch is only rendered once every branch
        // above it is open too, since each hangs off a row of its parent.
        let branch = n.branch;
        while (branch !== "trunk") {
            this.openBranches.add(branch);
            const base = this.byName.get(baseOf(branch));
            if (!base)
                break;
            branch = base.branch;
        }
        this.render();
        if (opts.push) {
            const url = new URL(window.location.href);
            url.searchParams.set(VERSION_PARAM, v);
            window.history.pushState({ [VERSION_PARAM]: v }, "", url);
        }
        if (opts.scroll) {
            // The site sets scroll-behavior: smooth, which "auto" would inherit;
            // arriving on a link should land straight on the version instead.
            // Centred on what is visible rather than on the window, so that the sheet
            // does not take the half the version was aimed at.
            this.scrollToSelected(opts.scroll);
        }
        else {
            this.keepSelectedClearOfSheet();
        }
    }
    bind() {
        this.el("otpv-tree").addEventListener("click", (e) => {
            const target = e.target;
            const major = target.closest(".otpv-major-btn");
            if (major) {
                const n = major.dataset.major;
                this.openMajors.has(n) ? this.openMajors.delete(n) : this.openMajors.add(n);
                this.renderTree();
                return;
            }
            const more = target.closest(".otpv-more-btn");
            if (more) {
                const id = more.dataset.branch;
                this.openBranches.has(id) ? this.openBranches.delete(id) : this.openBranches.add(id);
                this.renderTree();
                return;
            }
            const row = target.closest(".otpv-row");
            if (row)
                this.select(row.dataset.v, { push: true });
        });
        // Escape is the way out of anything laid over the page. The search box has
        // its own handler for closing its results; leave that key press to it.
        document.addEventListener("keydown", (e) => {
            if (e.key !== "Escape")
                return;
            if (e.target?.id === "otpv-q")
                return;
            if (this.sheetOpen && this.isSheet())
                this.closeSheet();
        });
        this.el("otpv-detail").addEventListener("click", (e) => {
            if (e.target.closest(".otpv-sheet-close")) {
                this.closeSheet();
                return;
            }
            const button = e.target.closest("[data-cve]");
            if (!button)
                return;
            const cve = button.dataset.cve;
            const showing = this.highlight && "cve" in this.highlight && this.highlight.cve === cve;
            this.setHighlight(showing ? null : { cve });
            // The advisory stays open either way; the button is inside it.
            this.expandedCves.add(cve);
            this.render();
        });
        // `toggle` does not bubble, so it is caught on the way down.
        this.el("otpv-detail").addEventListener("toggle", (e) => {
            const details = e.target;
            if (!details.classList?.contains("otpv-cve"))
                return;
            const cve = details.querySelector("[data-cve]")?.dataset.cve;
            if (!cve)
                return;
            if (details.open)
                this.expandedCves.add(cve);
            else
                this.expandedCves.delete(cve);
        }, true);
        this.el("otpv-expand").addEventListener("click", () => {
            this.majors.forEach((m) => this.openMajors.add(m.n));
            this.renderTree();
        });
        this.el("otpv-collapse").addEventListener("click", () => {
            this.openMajors.clear();
            this.renderTree();
        });
        const search = this.el("otpv-q");
        const results = this.el("otpv-hits");
        const runSearch = () => {
            // "OTP-28.5" is a release, "OTP-19180" is a ticket. Only strip the prefix
            // when what follows looks like a version, which is at most two digits
            // before the first dot; ticket numbers are far longer.
            const typed = search.value.trim().toLowerCase();
            const q = typed.replace(/^(erlang\/)?otp[- ]?(?=\d{1,2}(\.|$))/, "");
            if (!q) {
                results.innerHTML = "";
                if (this.highlight) {
                    this.setHighlight(null);
                    this.renderTree();
                }
                return;
            }
            const otp_hits = [];
            const app_hits = [];
            const cve_hits = [];
            const ticket_hits = [];
            const pr_hits = [];
            for (const n of this.nodes) {
                if (n.vsn.startsWith(q))
                    otp_hits.push({ label: "Erlang/OTP " + n.vsn, meta: n.date ?? "", version: n.vsn });
            }
            otp_hits.sort((a, b) => this.position.get(b.version) - this.position.get(a.version));
            for (const key of this.appKeys) {
                //console.log(key, q, key.toLowerCase().startsWith(q));
                if (key.toLowerCase().startsWith(q)) {
                    app_hits.push({ label: key, meta: `${this.appIndex.get(key).length} Erlang/OTP versions`, app: key });
                }
            }
            for (const [id, a] of Object.entries(this.data.cves)) {
                const cve = id.toLowerCase();
                const ghsa = (a.ghsa ?? "").toLowerCase();
                const summary = a.summary ?? "";
                // An id matches from the start freely, but only as a substring once the
                // query is long enough to mean something: a GHSA id is a random group
                // of letters, so "pr" is inside plenty of them. The wording is searched
                // from three characters in, for the same reason — otherwise "pr" turns
                // up everything that says "Predictable", which the result never shows.
                const found = cve.startsWith(typed) ||
                    ghsa.startsWith(typed) ||
                    (typed.length >= 4 && (cve.includes(typed) || ghsa.includes(typed))) ||
                    (typed.length >= 3 && summary.toLowerCase().includes(typed));
                if (found && !cve_hits.some((h) => h.cve === id)) {
                    const short = summary.length > 58 ? summary.slice(0, 58) + "\u2026" : summary;
                    cve_hits.push({ label: id, meta: `${a.severity ?? ""} · ${short}`, cve: id });
                }
            }
            // "#8699" is how a pull request or issue is written on github, so accept
            // that for the PR- and GH- ids as well as their spelled-out form.
            const issue = /^#(\d+)$/.exec(typed);
            for (const id of Object.keys(this.tickets)) {
                const lower = id.toLowerCase();
                const matches = issue
                    ? /^(pr|gh)-/.test(lower) && lower.slice(3).startsWith(issue[1])
                    : lower.includes(typed);
                if (!matches)
                    continue;
                const introduced = this.tickets[id];
                ticket_hits.push({
                    label: id,
                    meta: `first in ${introduced.slice(0, 3).join(", ")}${introduced.length > 3 ? "\u2026" : ""}`,
                    ticket: id,
                });
            }
            const truncated = [];
            if (Math.min(otp_hits.length, 1) +
                Math.min(app_hits.length, 1) +
                Math.min(cve_hits.length, 1) +
                Math.min(ticket_hits.length, 1) +
                Math.min(pr_hits.length, 1) > 1) {
                truncated.push({
                    label: "…",
                    meta: "Search results truncated to 10 hits per category. Please refine your search for more specific results.",
                });
                console.log("truncating search results", otp_hits.length, app_hits.length, cve_hits.length, ticket_hits.length, pr_hits.length);
                otp_hits.length = Math.min(otp_hits.length, 10); // set a limit of max 10 hits
                app_hits.length = Math.min(app_hits.length, 10); // set a limit of max 10 hits
                cve_hits.length = Math.min(cve_hits.length, 10); // set a limit of max 10 hits
                ticket_hits.length = Math.min(ticket_hits.length, 10); // set a limit of max 10 hits
                pr_hits.length = Math.min(pr_hits.length, 10); // set a limit of max 10 hits
            }
            this.hits = [...otp_hits, ...app_hits, ...cve_hits, ...ticket_hits, ...pr_hits, ...truncated];
            this.activeHit = -1;
            results.innerHTML = String(html `${this.hits.map((h, i) => html `
          <div class="otpv-hit" role="option" id="otpv-hit-${i}" aria-selected="false" data-i="${i}">
            <span class="otpv-hit-k">${h.label}</span>
            <span class="otpv-hit-m">${h.meta}</span>
          </div>`)}`);
            search.setAttribute("aria-expanded", this.hits.length ? "true" : "false");
            search.removeAttribute("aria-activedescendant");
        };
        const closeResults = () => {
            results.innerHTML = "";
            this.hits = [];
            this.activeHit = -1;
            search.setAttribute("aria-expanded", "false");
            search.removeAttribute("aria-activedescendant");
        };
        const moveActive = (delta) => {
            if (!this.hits.length)
                return;
            const previous = results.querySelector('[aria-selected="true"]');
            if (previous)
                previous.setAttribute("aria-selected", "false");
            this.activeHit = (this.activeHit + delta + this.hits.length) % this.hits.length;
            const option = results.querySelector(`#otpv-hit-${this.activeHit}`);
            if (option) {
                option.setAttribute("aria-selected", "true");
                option.scrollIntoView({ block: "nearest" });
                search.setAttribute("aria-activedescendant", option.id);
            }
        };
        search.addEventListener("input", runSearch);
        search.addEventListener("focus", runSearch);
        search.addEventListener("keydown", (e) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                moveActive(1);
            }
            else if (e.key === "ArrowUp") {
                e.preventDefault();
                moveActive(-1);
            }
            else if (e.key === "Escape") {
                closeResults();
            }
            else if (e.key === "Enter" && this.hits.length) {
                e.preventDefault();
                // With nothing highlighted, Enter takes the first result.
                activateHit(this.activeHit === -1 ? 0 : this.activeHit);
            }
        });
        const activateHit = (index) => {
            const hit = this.hits[index];
            if (!hit)
                return;
            closeResults();
            if (hit.version) {
                this.setHighlight(null);
                search.value = "";
                this.select(hit.version, { scroll: "smooth", push: true });
            }
            else if (hit.app) {
                this.setHighlight({ app: this.data.appVersions.indexOf(hit.app) });
                search.value = hit.app;
                const carrying = this.appIndex.get(hit.app);
                this.select([...carrying].sort((a, b) => this.position.get(a) - this.position.get(b))[0], { scroll: "smooth", push: true });
            }
            else if (hit.cve) {
                search.value = hit.cve;
                this.setHighlight({ cve: hit.cve });
                this.expandedCves.add(hit.cve);
                const affected = this.affectedBy(hit.cve);
                if (affected.length)
                    this.select(affected[affected.length - 1].vsn, { scroll: "smooth", push: true });
                else
                    this.renderTree();
            }
            else if (hit.ticket) {
                search.value = hit.ticket;
                this.setHighlight({ ticket: hit.ticket });
                // Land on the release that introduced it; the rest follow by ordering.
                const introduced = this.tickets[hit.ticket];
                if (introduced?.length)
                    this.select(introduced[0], { scroll: "smooth", push: true });
                else
                    this.renderTree();
            }
            else {
                // A bundled-component statement is a dismissal, so no release is
                // affected and there is nothing to pick out in the tree.
                search.value = hit.bundled;
                this.setHighlight(null);
                const majors = new Set(this.data.notAffected.filter((a) => a.id === hit.bundled).map((a) => a.major));
                const newest = this.nodes.filter((n) => majors.has(n.major)).pop();
                if (newest)
                    this.select(newest.vsn, { scroll: "smooth", push: true });
                else
                    this.renderTree();
            }
        };
        results.addEventListener("click", (e) => {
            const option = e.target.closest(".otpv-hit");
            if (option)
                activateHit(Number(option.dataset.i));
        });
        document.addEventListener("click", (e) => {
            if (!e.target.closest(".otpv-search"))
                closeResults();
        });
        // Going back should return to the version you were looking at.
        window.addEventListener("popstate", () => {
            const v = this.versionFromUrl() ?? this.nodes[this.nodes.length - 1].vsn;
            this.select(v, { scroll: "smooth", raise: false });
        });
    }
}
async function fetchJson(url) {
    const response = await fetch(url);
    if (!response.ok)
        throw new Error(`${url}: ${response.status} ${response.statusText}`);
    return (await response.json());
}
async function init() {
    const root = document.getElementById("otp-versions");
    if (!root)
        return;
    const url = root.dataset.src;
    if (!url)
        return;
    try {
        const [data, tickets] = await Promise.all([
            fetchJson(url),
            // Optional: the tree works without it, only the ticket search goes away.
            root.dataset.tickets ? fetchJson(root.dataset.tickets).catch(() => ({})) : {},
        ]);
        new VersionTree(data, tickets, root);
    }
    catch (error) {
        const status = document.getElementById("otpv-status");
        if (status) {
            status.textContent =
                "The version data could not be loaded, so the tree is unavailable. Reloading the page may help.";
        }
        console.error("otp-versions:", error);
    }
}
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
}
else {
    void init();
}
